import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { I18nProvider } from "@/i18n";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import Layout from "@/components/tmc/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Access from "@/pages/Access";
import ChangePassword from "@/pages/ChangePassword";
import Terms from "@/pages/Terms";
import TermsView from "@/pages/TermsView";
import HomeLanding from "@/pages/HomeLanding";
import Bots from "@/pages/Bots";
import TmcPanel from "@/pages/tmc/TmcPanel";
import PageBuilder from "@/pages/PageBuilder";
import PublicPage from "@/pages/PublicPage";

function FullLoader() {
  return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
}

function Protected({ children, adminOnly = false }) {
  const { user, hasAccess, loading, isAdmin } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
  if (!hasAccess && !isAdmin) return <Navigate to="/access" replace />;
  if (!user.terms_accepted) return <Navigate to="/terms" replace />;
  return children;
}

function AuthOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function TermsRoute() {
  const { user, hasAccess, loading, isAdmin } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!hasAccess && !isAdmin) return <Navigate to="/access" replace />;
  if (user.terms_accepted) return <Navigate to="/" replace />;
  return <Terms />;
}

function Home() {
  return <HomeLanding />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/regulamin" element={<TermsView />} />
      <Route path="/change-password" element={<AuthOnly><ChangePassword /></AuthOnly>} />
      <Route path="/access" element={<AuthOnly><Access /></AuthOnly>} />
      <Route path="/terms" element={<TermsRoute />} />
      <Route element={<Protected adminOnly><Layout /></Protected>}>
        <Route path="/bots" element={<Bots />} />
        <Route path="/strona" element={<PageBuilder />} />
        <Route path="/wnioski" element={<TmcPanel />} />
      </Route>
      <Route path="/:slug" element={<PublicPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-right" richColors />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
